// ---------------------------------------------------------------------------
// DnD Email Editor – Block Palette (unified – all blocks in one panel)
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useRef } from 'react';
import {
  BLOCK_CATEGORIES,
  getBlocksByCategory,
  isComponentRef,
  type BlockDef,
  type BlockTier,
  type SavedBlockEntry,
  type SavedBlockRef,
} from './dnd-blocks';

interface DndBlockPaletteProps {
  onDragStart: (blockId: string, tier: BlockTier) => void;
  onDragEnd: () => void;
  onClick: (blockId: string, tier: BlockTier) => void;
  /**
   * The saved-block library in palette order (header/footer defaults plus the
   * account's Email Components). The "Saved" category is rendered from this, so
   * an empty array hides the category entirely.
   */
  savedBlocks?: SavedBlockEntry[];
}

function PaletteBlock({
  block,
  onDragStart,
  onDragEnd,
  onClick,
}: {
  block: BlockDef;
  onDragStart: (blockId: string, tier: BlockTier) => void;
  onDragEnd: () => void;
  onClick: (blockId: string, tier: BlockTier) => void;
}) {
  const ghostRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', block.id);

      const ghost = document.createElement('div');
      ghost.className = 'dnd-drag-ghost';
      ghost.textContent = block.label;
      ghost.style.position = 'fixed';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
      ghostRef.current = ghost;

      onDragStart(block.id, block.tier);
    },
    [block.id, block.label, block.tier, onDragStart],
  );

  const handleDragEnd = useCallback(() => {
    if (ghostRef.current) {
      document.body.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
    onDragEnd();
  }, [onDragEnd]);

  const handleClick = useCallback(() => {
    onClick(block.id, block.tier);
  }, [block.id, block.tier, onClick]);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      className="dnd-palette-block"
      title={block.description}
    >
      <div className="dnd-palette-block-icon">
        <block.icon size={18} />
      </div>
      <div className="dnd-palette-block-label">{block.label}</div>
    </div>
  );
}

export function DndBlockPalette({
  onDragStart,
  onDragEnd,
  onClick,
  savedBlocks,
}: DndBlockPaletteProps) {
  // The three Saved tiles are static defs shown by availability: Header and
  // Footer when the account has authored them, and one Components tile — not one
  // per component — when the library is non-empty.
  const savedDefs = useMemo(() => {
    const entries = savedBlocks ?? [];
    const has = (ref: SavedBlockRef) => entries.some((e) => e.ref === ref);
    const hasComponents = entries.some((e) => isComponentRef(e.ref));

    return getBlocksByCategory('saved').filter((b) =>
      b.opensComponentPicker ? hasComponents : !!b.savedRef && has(b.savedRef),
    );
  }, [savedBlocks]);

  return (
    <div className="dnd-block-palette">
      {BLOCK_CATEGORIES.map((cat) => {
        const blocks = cat.key === 'saved' ? savedDefs : getBlocksByCategory(cat.key);
        if (!blocks.length) return null;
        return (
          <div key={cat.key} className="dnd-palette-category">
            <div className="dnd-palette-category-label">{cat.label}</div>
            <div className="dnd-palette-grid">
              {blocks.map((block) => (
                <PaletteBlock
                  key={block.id}
                  block={block}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onClick={onClick}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
